import React, {
  useState,
  ChangeEventHandler,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import {
  useHideOverflowing,
  useHideWhenClipped,
} from 'components/common/useHideOverflowing';
import GlobalSearch from 'components/common/GlobalSearch';
import { RefreshSpinner } from 'components/common/ProjectIcons';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import SelectInput from 'components/common/SelectInput';
import { useTranslation } from 'react-i18next';
import { DateRange, LogLevel, LogTypes } from 'components/common/ObjectTypes';
import { isValidDuration } from 'utils/parseDuration';
import {
  filterClasses,
  buttonClasses,
  onTealText,
} from 'components/common/tailwindClassnames';
import { LOG_LEVEL_ICONS } from './logLevelIcons';

type LogLevelOption = { label: string; value: string; [key: string]: any };

const formatLogLevelOption = (option: LogLevelOption) => (
  <div className="flex items-center gap-2">
    {LOG_LEVEL_ICONS[option.value]}
    <span>{option.label}</span>
  </div>
);

const SESSION_STORAGE_KEY = 'routeLogsDateFilter';

function parseOffsetMinutes(offset: string): number {
  const match = offset.match(/^([+-])(\d{1,2}):?(\d{2})$/);
  if (!match) return 0;
  const sign = match[1] === '+' ? 1 : -1;
  return sign * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10));
}

function toTzDisplay(utcDate: Date, offsetStr: string): Date {
  const targetOffsetMin = parseOffsetMinutes(offsetStr);
  const browserOffsetMin = -utcDate.getTimezoneOffset();
  const diffMs = (targetOffsetMin - browserOffsetMin) * 60 * 1000;
  return new Date(utcDate.getTime() + diffMs);
}

function fromTzDisplay(displayDate: Date, offsetStr: string): Date {
  const targetOffsetMin = parseOffsetMinutes(offsetStr);
  const browserOffsetMin = -displayDate.getTimezoneOffset();
  const diffMs = (targetOffsetMin - browserOffsetMin) * 60 * 1000;
  return new Date(displayDate.getTime() - diffMs);
}

function formatTzDate(utcDate: Date, offsetStr: string): string {
  const d = toTzDisplay(utcDate, offsetStr);
  const mon = d.toLocaleString('en', { month: 'short' });
  const day = d.getDate();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${mon} ${day} ${hh}:${mm}`;
}

type LogsFilterProps = {
  globalSearchStr?: string;
  selectedTimezone: any;
  onChangeGlobalSearch: (value: string) => void;
  onResetFilters: any;
  onChangeStatusFilter: ChangeEventHandler;
  onChangeLogType: ChangeEventHandler;
  onChangeLogDate: Function;
  onRefresh: Function;
  reloadLogs: boolean;
  // GUNDI-5679-ish (tech-lead ask): when the Route is unhealthy, the logs
  // open pre-filtered to "Warning" minimum so the signal is on top. Applied
  // once per mount, and only while no level filter is active — after that
  // the filter behaves exactly like one the user picked.
  defaultMinimumWarning?: boolean;
  hasActiveLogLevel?: boolean;
  // The parent's CURRENT log-level filter ({name, id} or null). This filter
  // panel remounts on every tab switch while the parent's filter state
  // survives, so without seeding from it the dropdown showed empty over an
  // active filter (user-visible once the unhealthy default landed).
  activeLogLevel?: { name: string; id: string } | null;
  // The parent's CURRENT log type id ("cdc" = Changes, "ev" = Activity) or null.
  activeLogType?: string | null;
};

const LogsFilter = ({
  globalSearchStr,
  selectedTimezone,
  onChangeGlobalSearch,
  onResetFilters,
  onChangeStatusFilter,
  onChangeLogType,
  onChangeLogDate,
  onRefresh,
  reloadLogs,
  defaultMinimumWarning,
  hasActiveLogLevel,
  activeLogLevel,
  activeLogType,
}: LogsFilterProps) => {
  // Phones: a control that does not fully fit in its row is hidden, not cut in half.
  const searchRowRef = useHideOverflowing<HTMLDivElement>();
  const filterRowRef = useHideOverflowing<HTMLDivElement>();
  // The suggestion chips show only when all of them fit on one line.
  const suggestionsRef = useHideWhenClipped<HTMLDivElement>([globalSearchStr]);
  const { t } = useTranslation();
  const defaultDateRange = DateRange.find(
    (option) => option.label === 'Past 3 hours',
  );
  const [selectedLogType, setSelectedLogType] = useState<any>(null);
  // Seeded from the parent's surviving filter so a remount (tab switch)
  // shows what is actually applied.
  const [selectedLogLevel, setSelectedLogLevel] = useState<any>(() =>
    activeLogLevel
      ? (LogLevel.find((option) => option.value === activeLogLevel.id) ?? null)
      : null,
  );
  const [selectedDateRange, setSelectedDateRange] = useState<any>(
    defaultDateRange || null,
  );

  const [showCustomPanel, setShowCustomPanel] = useState(false);
  // The Date Range dropdown and its custom panel; a click anywhere else closes
  // the panel.
  const dateRangeRef = useRef<HTMLDivElement>(null);
  // With a time list shown, the date picker keeps its calendar open after a
  // day is clicked; these close it so the panel's other field is reachable.
  const startPickerRef = useRef<DatePicker>(null);
  const endPickerRef = useRef<DatePicker>(null);
  const [relativeTimeInput, setRelativeTimeInput] = useState('');
  const [relativeTimeError, setRelativeTimeError] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dateError, setDateError] = useState('');
  const [dateSearchInput, setDateSearchInput] = useState('');

  const tzOffset = selectedTimezone?.value || '+00:00';

  const saveFilterToSession = useCallback((data: Record<string, any>) => {
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
    } catch {
      /* ignore quota errors */
    }
  }, []);

  const clearFilterSession = useCallback(() => {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  }, []);

  // Restore saved filter from sessionStorage on mount
  useEffect(() => {
    let restored = false;
    try {
      const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved.type === 'preset' && saved.value) {
          const preset = DateRange.find((o) => o.value === saved.value);
          if (preset) {
            setSelectedDateRange(preset);
            onChangeLogDate(preset, 'from_date');
            onChangeLogDate(null, 'to_date');
            restored = true;
          }
        } else if (saved.type === 'relative' && saved.input) {
          setSelectedDateRange({
            label: `Past ${saved.input}`,
            value: 'custom',
          });
          setRelativeTimeInput(saved.input);
          onChangeLogDate({ value: saved.input }, 'from_date');
          onChangeLogDate(null, 'to_date');
          restored = true;
        } else if (saved.type === 'custom' && saved.startDate) {
          const sd = new Date(saved.startDate);
          setStartDate(sd);
          onChangeLogDate({ value: sd }, 'from_date');
          const savedOffset = selectedTimezone?.value || '+00:00';
          if (saved.endDate) {
            const ed = new Date(saved.endDate);
            setEndDate(ed);
            onChangeLogDate({ value: ed }, 'to_date');
            const startLabel = formatTzDate(sd, savedOffset);
            const endLabel = formatTzDate(ed, savedOffset);
            setSelectedDateRange({
              label: `${startLabel} – ${endLabel}`,
              value: 'custom',
            });
          } else {
            const startLabel = formatTzDate(sd, savedOffset);
            setSelectedDateRange({ label: `${startLabel} –`, value: 'custom' });
          }
          restored = true;
        }
      }
    } catch {
      /* ignore parse errors */
    }

    if (!restored && defaultDateRange) {
      onChangeLogDate(defaultDateRange, 'from_date');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The timezone can change in the nav while a custom range is set. The
  // pickers follow it on their own; the range's label is text built when the
  // dates were picked, so rebuild it in the new timezone. Only a picked range
  // has a startDate — relative and preset ranges clear it.
  useEffect(() => {
    if (!startDate) return;
    const startLabel = formatTzDate(startDate, tzOffset);
    setSelectedDateRange({
      label: endDate
        ? `${startLabel} – ${formatTzDate(endDate, tzOffset)}`
        : `${startLabel} –`,
      value: 'custom',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tzOffset]);

  useEffect(() => {
    if (!showCustomPanel) return;
    const closeOnOutsideClick = (e: MouseEvent) => {
      if (!dateRangeRef.current?.contains(e.target as Node))
        setShowCustomPanel(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [showCustomPanel]);

  const handleDateSearchInputChange = (inputValue: string) => {
    setDateSearchInput(inputValue);
  };

  const handleDateRangeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    const value = dateSearchInput.trim();
    if (!value || !isValidDuration(value)) return;

    e.preventDefault();
    setRelativeTimeError('');
    setStartDate(null);
    setEndDate(null);
    onChangeLogDate({ value }, 'from_date');
    onChangeLogDate(null, 'to_date');
    setSelectedDateRange({ label: `Past ${value}`, value: 'custom' });
    setShowCustomPanel(false);
    setDateSearchInput('');
    saveFilterToSession({ type: 'relative', input: value });

    (e.target as HTMLElement).blur();
  };

  const dateRangeNoOptionsMessage = ({
    inputValue,
  }: {
    inputValue: string;
  }) => {
    if (inputValue && isValidDuration(inputValue.trim())) {
      const value = t('Press Enter to apply');
      return value + ` "${inputValue.trim()}"`;
    }
    return t('No options');
  };

  const handleLogTypeChange = (e: any) => {
    setSelectedLogType(e);
    // Selecting Changes clears the level filter (see filterLogType).
    if (e?.value === 'cdc') setSelectedLogLevel(null);
    onChangeLogType(e);
  };

  const handleLogLevelChange = (e: any) => {
    setSelectedLogLevel(e);
    onChangeStatusFilter(e);
  };

  // The parent can also apply a level AFTER this panel mounted — the
  // unhealthy-Route default is seeded when the route fetch resolves, which is
  // later than this panel's useState seed ran. Backfill an EMPTY dropdown
  // from the applied filter; fill-only, so it can never clear or overwrite a
  // selection the user made here.
  useEffect(() => {
    if (activeLogLevel && !selectedLogLevel) {
      setSelectedLogLevel(
        LogLevel.find((option) => option.value === activeLogLevel.id) ?? null,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLogLevel?.id]);

  // Apply the unhealthy-Route default at most ONCE per mount, and never over
  // an existing selection: filterLogStatus() TOGGLES a level that is already
  // in the parent's filter, so re-applying "Warning" blindly would turn the
  // filter off while the select still shows it.
  const appliedDefaultRef = useRef(false);
  useEffect(() => {
    if (appliedDefaultRef.current) return;
    // Changes logs carry no level, so the default is never applied over them,
    // and it counts as used: switching on to Activity later must not bring
    // Warning in on its own.
    if (activeLogType === 'cdc') {
      appliedDefaultRef.current = true;
      return;
    }
    if (!defaultMinimumWarning) return;
    // A level already in place (the parent seeds this same default before the
    // panel mounts) counts as the default applied. Without this, clearing it
    // later, by hand or by choosing Changes, flipped hasActiveLogLevel and
    // brought Warning straight back.
    appliedDefaultRef.current = true;
    if (hasActiveLogLevel || selectedLogLevel) return;
    const warning = LogLevel.find((option) => option.value === '30');
    if (warning) handleLogLevelChange(warning);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultMinimumWarning, hasActiveLogLevel, activeLogType]);

  const handleDateRangeChange = (e: any) => {
    setSelectedDateRange(e);

    if (!e) {
      setShowCustomPanel(false);
      setRelativeTimeInput('');
      setRelativeTimeError('');
      setStartDate(null);
      setEndDate(null);
      onChangeLogDate(null, 'from_date');
      onChangeLogDate(null, 'to_date');
      clearFilterSession();
      return;
    }

    if (e.value === 'custom') {
      setShowCustomPanel(true);
      saveFilterToSession({ type: 'preset', value: 'custom' });
      return;
    }

    setShowCustomPanel(false);
    setRelativeTimeInput('');
    setRelativeTimeError('');
    setStartDate(null);
    setEndDate(null);
    onChangeLogDate(e, 'from_date');
    onChangeLogDate(null, 'to_date');
    saveFilterToSession({ type: 'preset', value: e.value, label: e.label });
  };

  const handleApplyRelativeTime = () => {
    const value = relativeTimeInput.trim();
    if (!value) return;

    if (!isValidDuration(value)) {
      const error_message = t('Invalid format. Use e.g. 1h, 90m, 60s, 1d, 2w');
      setRelativeTimeError(error_message);
      return;
    }

    setRelativeTimeError('');
    setStartDate(null);
    setEndDate(null);
    onChangeLogDate({ value }, 'from_date');
    onChangeLogDate(null, 'to_date');
    setSelectedDateRange({ label: `Past ${value}`, value: 'custom' });
    setShowCustomPanel(false);
    saveFilterToSession({ type: 'relative', input: value });
  };

  const handleRelativeTimeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleApplyRelativeTime();
    }
  };

  const handleStartDateChange = (displayDate: Date) => {
    const utcDate = fromTzDisplay(displayDate, tzOffset);
    setStartDate(utcDate);
    setRelativeTimeInput('');
    setRelativeTimeError('');
    setDateError('');
    onChangeLogDate({ value: utcDate }, 'from_date');
    const startLabel = formatTzDate(utcDate, tzOffset);
    if (endDate && utcDate > endDate) {
      setEndDate(null);
      onChangeLogDate(null, 'to_date');
      setSelectedDateRange({ label: `${startLabel} –`, value: 'custom' });
      saveFilterToSession({
        type: 'custom',
        startDate: utcDate.toISOString(),
      });
    } else if (endDate) {
      const endLabel = formatTzDate(endDate, tzOffset);
      setSelectedDateRange({
        label: `${startLabel} – ${endLabel}`,
        value: 'custom',
      });
      saveFilterToSession({
        type: 'custom',
        startDate: utcDate.toISOString(),
        endDate: endDate.toISOString(),
      });
    } else {
      setSelectedDateRange({ label: `${startLabel} –`, value: 'custom' });
      saveFilterToSession({
        type: 'custom',
        startDate: utcDate.toISOString(),
      });
    }
  };

  const handleEndDateChange = (displayDate: Date) => {
    const utcDate = fromTzDisplay(displayDate, tzOffset);
    if (startDate && utcDate < startDate) {
      const error = t('End date must be after start date');
      setDateError(error);
      return;
    }
    setEndDate(utcDate);
    setRelativeTimeInput('');
    setRelativeTimeError('');
    setDateError('');
    onChangeLogDate({ value: utcDate }, 'to_date');
    const endLabel = formatTzDate(utcDate, tzOffset);
    const startLabel = startDate ? formatTzDate(startDate, tzOffset) : '';
    setSelectedDateRange({
      label: `${startLabel} – ${endLabel}`,
      value: 'custom',
    });
    setShowCustomPanel(false);
    saveFilterToSession({
      type: 'custom',
      startDate: startDate ? startDate.toISOString() : '',
      endDate: utcDate.toISOString(),
    });
  };

  const handleResetFilters = () => {
    setSelectedLogType(null);
    setSelectedLogLevel(null);
    setShowCustomPanel(false);
    setRelativeTimeInput('');
    setRelativeTimeError('');
    setDateError('');
    setStartDate(null);
    setEndDate(null);

    onResetFilters();
    clearFilterSession();

    const defaultOption = DateRange.find(
      (option) => option.label === 'Past 3 hours',
    );
    if (defaultOption) {
      setSelectedDateRange(defaultOption);
      onChangeLogDate(defaultOption, 'from_date');
    }
  };

  return (
    <div className="mb-3 mr-3 h-auto w-full bg-teal-t10 px-3 pb-3 text-ui-text-primary-text transition-all duration-300">
      {/* Search row */}
      <div
        ref={searchRowRef}
        className="mb-3 flex flex-row flex-nowrap items-end gap-3 overflow-x-clip pt-3"
      >
        <GlobalSearch
          onChangeGlobalSearch={onChangeGlobalSearch}
          savedString={globalSearchStr}
        />
        {/* Never wraps: hidden whenever the chips do not all fit on one line. */}
        {!globalSearchStr && (
          <div
            ref={suggestionsRef}
            className="mb-2 flex min-w-0 flex-1 flex-row flex-nowrap items-center gap-1 overflow-hidden"
          >
            <span className="shrink-0 text-[15px] text-ui-text-secondary-text">
              {t('Try')}
            </span>
            {[
              { label: t('delivered'), value: 'delivered' },
              { label: t('source'), value: 'source' },
              { label: t('event'), value: 'event' },
              { label: t('observation'), value: 'observation' },
            ].map(({ label, value }) => (
              <button
                key={value}
                type="button"
                onClick={() => onChangeGlobalSearch(value)}
                // Per design: 3px radius, 1px Primary Text border on
                // white — the same chip shape as the filter controls
                // beside it rather than a lozenge.
                className="shrink-0 whitespace-nowrap rounded-[3px] border border-ui-text-primary-text bg-ui-background-primary-background px-2 py-0.5 text-[14px] leading-[21px] text-ui-text-primary-text transition-colors duration-300 hover:bg-teal-t10"
              >
                {label}
              </button>
            ))}
          </div>
        )}
        {/* Reload sits immediately left of Clear All Filters, the same
                    order as every list page. It used to live in the dropdown row
                    below, which put the two controls on different lines. */}
        <button
          type="button"
          onClick={() => onRefresh()}
          aria-label={t('Refresh logs' as any)}
          disabled={reloadLogs}
          className={`mb-2 ml-auto flex h-[38px] w-8 cursor-pointer items-center justify-center self-end ${reloadLogs ? 'origin-center animate-spin' : ''} transition-colors duration-300`}
        >
          <RefreshSpinner />
        </button>
        <button
          className={`${buttonClasses.clearFilters} mb-2 whitespace-nowrap`}
          onClick={handleResetFilters}
        >
          {t('Clear All Filters')}
        </button>
      </div>

      {/* Filters row */}
      <div className="flex flex-row items-start justify-between">
        {/* flex-1 min-w-0: as a flex item this row would otherwise grow to fit all
                    its dropdowns, so none of them overflowed it and none got hidden.
                    Full width also leaves room for the custom date panel on desktop. */}
        <div
          ref={filterRowRef}
          className="flex min-w-0 flex-1 flex-row flex-nowrap gap-x-2 overflow-x-clip"
        >
          {/* log type */}
          <div>
            <label className={onTealText}>{t('Log Type')}</label>
            <SelectInput
              options={LogTypes.map((option) => ({
                ...option,
                label: t(option.label, option.label),
              }))}
              data-testid="select_timezone_activity_logs_filter"
              className={'basic-single w-60 pb-1 text-sm'}
              defaultValue={undefined}
              value={
                selectedLogType
                  ? {
                      ...selectedLogType,
                      label: t(selectedLogType.label, selectedLogType.label),
                    }
                  : null
              }
              isDisabled={false}
              isLoading={false}
              isClearable={true}
              isSearchable={true}
              isMulti={false}
              name={'Filter by Organization'}
              onChange={handleLogTypeChange}
              variant="routeFilter"
            />
          </div>
          {/* status */}
          <div>
            <label className={onTealText}>{t('Minimum Log Level')}</label>
            <SelectInput
              options={LogLevel.map((option) => ({
                ...option,
                label: t(option.label, option.label),
              }))}
              data-testid="select_timezone_activity_logs_filter"
              className={'basic-single w-60 pb-1 text-sm'}
              defaultValue={undefined}
              value={
                selectedLogLevel
                  ? {
                      ...selectedLogLevel,
                      label: t(selectedLogLevel.label, selectedLogLevel.label),
                    }
                  : null
              }
              isDisabled={false}
              isLoading={false}
              isClearable={true}
              isSearchable={true}
              isMulti={false}
              name={'Filter by Organization'}
              onChange={handleLogLevelChange}
              variant="routeFilter"
              formatOptionLabel={formatLogLevelOption}
            />
          </div>
          {/* date & time */}
          <div className="relative" ref={dateRangeRef}>
            <label className={onTealText}>{t('Date Range')}</label>
            <SelectInput
              options={DateRange.map((option) => ({
                ...option,
                label: t(option.label, option.label),
              }))}
              data-testid="select_timezone_activity_logs_filter"
              className={'basic-single w-80 pb-1 text-sm'}
              value={
                selectedDateRange
                  ? {
                      ...selectedDateRange,
                      label: t(
                        selectedDateRange.label,
                        selectedDateRange.label,
                      ),
                    }
                  : null
              }
              isDisabled={false}
              isLoading={false}
              isClearable={true}
              isSearchable={true}
              isMulti={false}
              name={'Date Range'}
              onChange={handleDateRangeChange}
              variant="routeFilter"
              handleInputChange={handleDateSearchInputChange}
              onKeyDown={handleDateRangeKeyDown}
              noOptionsMessage={dateRangeNoOptionsMessage}
            />
            {showCustomPanel && (
              <div className="absolute left-0 top-full z-20 mt-2 w-80 rounded border border-unlisted-gray-lines bg-unlisted-very-light-gray p-3 transition-colors duration-300">
                <label className="text-xs text-ui-text-primary-text">
                  {t('Relative time')}
                </label>
                <div className="mt-1 flex flex-row gap-1">
                  <input
                    type="text"
                    value={relativeTimeInput}
                    onChange={(e) => {
                      setRelativeTimeInput(e.target.value);
                      setRelativeTimeError('');
                    }}
                    onKeyDown={handleRelativeTimeKeyDown}
                    placeholder={t('e.g. 1h, 90m, 60s, 1d')}
                    className="min-w-0 flex-1 rounded-sm border border-unlisted-gray-lines p-1 text-sm transition-colors duration-300"
                  />
                  <button
                    onClick={handleApplyRelativeTime}
                    className={`${buttonClasses.lightGreen} text-sm`}
                  >
                    {t('Apply')}
                  </button>
                </div>
                {relativeTimeError && (
                  <p className="mt-1 text-xs text-ui-status-error-destructive">
                    {relativeTimeError}
                  </p>
                )}
                <div className="my-3 flex items-center">
                  <div className="flex-1 border-t border-unlisted-gray-lines"></div>
                  <span className="px-2 text-xs text-ui-text-primary-text">
                    {t('or')}
                  </span>
                  <div className="flex-1 border-t border-unlisted-gray-lines"></div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col">
                    <label className="text-xs text-ui-text-primary-text">
                      {t('Start')}
                    </label>
                    <DatePicker
                      ref={startPickerRef}
                      selected={
                        startDate ? toTzDisplay(startDate, tzOffset) : null
                      }
                      onChange={handleStartDateChange}
                      onSelect={() => startPickerRef.current?.setOpen(false)}
                      showTimeSelect
                      timeIntervals={15}
                      className="mt-1 w-full rounded-sm border border-unlisted-gray-lines p-1 text-sm transition-colors duration-300"
                      dateFormat="yyyy-MM-dd HH:mm"
                      placeholderText={t('Select start date')}
                    />
                  </div>
                  <div className="flex flex-col">
                    <label className="text-xs text-ui-text-primary-text">
                      {t('End')}
                    </label>
                    <DatePicker
                      ref={endPickerRef}
                      selected={endDate ? toTzDisplay(endDate, tzOffset) : null}
                      minDate={
                        startDate ? toTzDisplay(startDate, tzOffset) : undefined
                      }
                      onChange={handleEndDateChange}
                      onSelect={() => endPickerRef.current?.setOpen(false)}
                      disabled={!startDate}
                      showTimeSelect
                      timeIntervals={15}
                      className="mt-1 w-full rounded-sm border border-unlisted-gray-lines p-1 text-sm transition-colors duration-300 disabled:cursor-not-allowed disabled:opacity-50"
                      dateFormat="yyyy-MM-dd HH:mm"
                      placeholderText={t('Select end date')}
                    />
                  </div>
                </div>
                {dateError && (
                  <p className="mt-1 text-xs text-ui-status-error-destructive">
                    {dateError}
                  </p>
                )}
                <p className="mb-1 mt-2 text-xs text-ui-text-primary-text">
                  {selectedTimezone?.label || 'UTC'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogsFilter;
